import React, { useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Underline from "./extensions/Underline";
import FontSize from "./extensions/FontSize";
import BulletListCustom from "./extensions/BulletListCustom";
import OrderedListCustom from "./extensions/OrderedListCustom";
import "./RichTextEditor.css";

const RichTextEditor = forwardRef(
  (
    { content = "", onChange, onUpdate, onReady, onBlur, placeholder = "Start typing..." },
    ref
  ) => {
    const onBlurRef = useRef(onBlur);
    onBlurRef.current = onBlur;

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4, 5, 6],
          },
          bulletList: false,
          orderedList: false,
          horizontalRule: false,
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer",
          },
        }),
        Image.configure({
          inline: true,
          allowBase64: false,
        }),
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Placeholder.configure({
          placeholder: placeholder,
        }),
        TextStyle,
        Color,
        Highlight.configure({
          multicolor: true,
        }),
        Underline,
        FontSize,
        BulletListCustom,
        OrderedListCustom,
      ],
      content,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        if (onChange) {
          onChange(html);
        }
        if (onUpdate) {
          onUpdate({ editor });
        }
      },
      editorProps: {
        attributes: {
          class: "ProseMirror",
        },
        handleDOMEvents: {
          blur: () => {
            onBlurRef.current?.();
            return false;
          },
        },
      },
    });

    useEffect(() => {
      if (editor && onReady) {
        onReady(editor);
      }
    }, [editor, onReady]);

    useEffect(() => {
      if (editor && content !== editor.getHTML()) {
        editor.commands.setContent(content);
      }
    }, [content, editor]);

    useImperativeHandle(ref, () => editor, [editor]);

    if (!editor) {
      return null;
    }

    return (
      <div className="rich-text-editor-wrapper">
        <div className="rich-text-editor-content">
          <EditorContent editor={editor} />
        </div>
      </div>
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
